import React from "react";
import { ROUTES_CONFIG } from "src/constants/routesConfig";
import { TransitPurchaseOrderColumnHeader } from "src/enums";
import { TransitPurchaseOrder, TableColumn } from "src/types";
import { StatusPill } from "src/components/ui";
import { Table } from "src/components";
import { Link } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "src/shadcn/ui/tooltip";
import { Edit, Info, ShieldAlert, ExternalLink } from "lucide-react";
import { TrackingLink } from "src/components";
import { useAuthorisation } from "src/context";
import { RoleHelper } from "src/helpers";
import { TransitPurchaseOrderFulfilmentCompletionCell } from "./shared/transitPurchaseOrderFulfilmentCompletionCell";
import { formatEstimatedDeliveryDate, formatActualDeliveryDate } from "./shared/dateFormatters";

interface TransitPurchaseOrdersDashTableProps {
  transitPurchaseOrders: TransitPurchaseOrder[];
  columnsToHide?: TransitPurchaseOrderColumnHeader[];
}

export const TransitPurchaseOrdersDashTable: React.FC<TransitPurchaseOrdersDashTableProps> = ({
  transitPurchaseOrders,
  columnsToHide
}) => {
  const { userRole } = useAuthorisation();
  const transitPurchaseOrdersTableColumns: TableColumn<TransitPurchaseOrderColumnHeader, TransitPurchaseOrder>[] = [
    {
      header: TransitPurchaseOrderColumnHeader.PURCHASE_ORDER,
      column: (row: TransitPurchaseOrder) => (
        <Link
          to={`${ROUTES_CONFIG.purchaseOrders.path}?id=${row.id}`}
          className="text-primary-900 hover:text-primary-700 hover:underline transition-colors"
        >
          {row.id}
        </Link>
      )
    },
    {
      header: TransitPurchaseOrderColumnHeader.TRANSIT_STATUS,
      column: (row: TransitPurchaseOrder) => (
        <span className="flex justify-center">
          <StatusPill status={row.transitStatus} />
        </span>
      ),
      styles: { textAlign: "center" }
    },
    {
      header: TransitPurchaseOrderColumnHeader.CONFIRMATION_STATUS,
      column: (row: TransitPurchaseOrder) => (
        <span className="flex justify-center">
          {row.confirmationStatus && <StatusPill status={row.confirmationStatus} />}
        </span>
      ),
      styles: { textAlign: "center" }
    },
    {
      header: TransitPurchaseOrderColumnHeader.COMPLETION_STATUS,
      column: (row: TransitPurchaseOrder) => (
        <span className="flex justify-center">
          {row.completionStatus && <StatusPill status={row.completionStatus} />}
        </span>
      ),
      styles: { textAlign: "center" }
    },
    {
      header: TransitPurchaseOrderColumnHeader.TRACKING_LINK,
      column: (row: TransitPurchaseOrder) =>
        row.trackingNo ? (
          <div className="flex justify-center">
            <TrackingLink trackingNo={row.trackingNo} carrier={row.carrier} collectionOrder={row.collectionOrder} />
          </div>
        ) : (
          row.trackingNo
        ),
      styles: { textAlign: "center" }
    },
    {
      header: TransitPurchaseOrderColumnHeader.ESTIMATED_DELIVERY_DATE,
      column: (row: TransitPurchaseOrder) =>
        formatEstimatedDeliveryDate(
          row.carrierEstimatedDeliveryDate,
          row.confirmationDetails?.estDeliveryDate,
          row.estimatedDeliveryDate
        ),
      styles: { textAlign: "center" }
    },
    {
      header: TransitPurchaseOrderColumnHeader.ACTUAL_DELIVERY_DATE,
      column: (row: TransitPurchaseOrder) => formatActualDeliveryDate(row.actualDeliveryDate),
      styles: { textAlign: "center" }
    },
    {
      header: TransitPurchaseOrderColumnHeader.FULFILMENT_COMPLETION,
      column: (row: TransitPurchaseOrder) => (
        <TransitPurchaseOrderFulfilmentCompletionCell
          transitPurchaseOrder={row}
          canViewWarehouseProgress={RoleHelper.isHealfTeam(userRole) || userRole === "WAREHOUSE"}
        />
      ),
      styles: { minWidth: "300px" }
    },
    {
      header: TransitPurchaseOrderColumnHeader.ACTION,
      column: (row: TransitPurchaseOrder) => <span className="text-sm text-muted-700">{row.action || "-"}</span>
    },
    {
      header: TransitPurchaseOrderColumnHeader.MATCH_STATUS,
      column: (row: TransitPurchaseOrder) => <span className="text-sm text-muted-700">{row.matchStatus || "-"}</span>
    },
    {
      header: TransitPurchaseOrderColumnHeader.POTENTIAL_MATCHES,
      column: (row: TransitPurchaseOrder) => {
        if (!row.potentialMatches || row.potentialMatches.length === 0) {
          return <span className="text-muted-500">-</span>;
        }

        // Sort by confidence score (highest first)
        const sortedMatches = [...row.potentialMatches].sort((a, b) => b.confidenceScore - a.confidenceScore);

        return (
          <div className="flex flex-col gap-1 min-w-[200px]">
            {sortedMatches.map((match, index) => (
              <Link
                key={index}
                to={`${ROUTES_CONFIG.purchaseOrders.path}?id=${match.checkInId}`}
                className="text-primary-900 hover:text-primary-700 hover:underline transition-colors text-sm flex items-center gap-1"
              >
                <span>
                  {match.checkInId} ({match.confidenceScore}pt)
                </span>
                <ExternalLink className="h-3 w-3 flex-shrink-0" />
              </Link>
            ))}
          </div>
        );
      },
      styles: { minWidth: "220px" }
    },
    {
      header: TransitPurchaseOrderColumnHeader.VENDOR,
      column: (row: TransitPurchaseOrder) => row.vendorName
    },
    {
      header: TransitPurchaseOrderColumnHeader.SENDER_EMAIL,
      column: (row: TransitPurchaseOrder) => row.senderEmail
    },
    {
      header: TransitPurchaseOrderColumnHeader.ACTIONS,
      column: (row: TransitPurchaseOrder) => (
        <TooltipProvider>
          <div className="flex items-center justify-center gap-1">
            {/* More Info */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to={`${ROUTES_CONFIG.purchaseOrders.path}?id=${row.id}`}
                  className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md hover:bg-secondary-100 transition-colors"
                >
                  <Info className="h-4 w-4 text-muted-600" />
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p>More Info</p>
              </TooltipContent>
            </Tooltip>

            {/* Edit */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to={`${ROUTES_CONFIG.amendTransitPurchaseOrder.path}?id=${row.id}`}
                  className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md hover:bg-secondary-100 transition-colors"
                >
                  <Edit className="h-4 w-4 text-muted-600" />
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p>Amend Transit Purchase Order</p>
              </TooltipContent>
            </Tooltip>

            {/* Create Claim */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to={`${ROUTES_CONFIG.createClaim.path}?id=${row.id}&issueType=discrepancy`}
                  className="inline-flex items-center justify-center h-8 w-8 p-0 rounded-md hover:bg-secondary-100 transition-colors"
                >
                  <ShieldAlert className="h-4 w-4 text-muted-600" />
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p>Create Discrepancy Claim</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      ),
      styles: { textAlign: "center" }
    }
  ];

  return (
    <Table data={transitPurchaseOrders} columns={transitPurchaseOrdersTableColumns} columnsToHide={columnsToHide} />
  );
};
